import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  ShieldAlert,
  Zap,
  Activity,
  Layers,
  CheckCircle2,
  AlertTriangle,
  Globe,
  Sliders,
  BarChart3,
  Lock,
  RefreshCw,
  Power
} from 'lucide-react';
import { AppView, MonetizationMode } from '../../types';
import { monetizationEngine } from '../../services/monetizationEngine';

interface Props {
  onNavigateToView?: (view: AppView) => void;
}

export const MonetizationCommandCenter: React.FC<Props> = ({ onNavigateToView }) => {
  const [telemetry, setTelemetry] = useState(monetizationEngine.getTelemetry());
  const [testModeActive, setTestModeActive] = useState(telemetry.activeMode === 'TEST_MODE');

  const rankingCheck = monetizationEngine.runOrganicRankingIndependenceCheck();
  const protectedZoneCheck = monetizationEngine.runProtectedCalculationZoneCheck();
  const privacyCheck = monetizationEngine.runFinancialInputPrivacyCheck();

  const handleModeChange = (mode: MonetizationMode) => {
    monetizationEngine.setMonetizationMode(mode);
    setTelemetry(monetizationEngine.getTelemetry());
    setTestModeActive(mode === 'TEST_MODE');
  };

  const handleKillSwitch = () => {
    if (confirm('EMERGENCY KILL-SWITCH: Are you sure you want to disable all display ads instantly across all page types and markets?')) {
      monetizationEngine.emergencyKillSwitchAllAds();
      setTelemetry(monetizationEngine.getTelemetry());
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Executive Banner */}
      <div className="bg-slate-900 text-white rounded-2xl p-8 shadow-xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/30">
              <DollarSign className="w-3.5 h-3.5" />
              YamooPay Revenue Operations & Commercial Intelligence
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight">Monetization Command Center</h1>
            <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
              Global revenue intelligence orchestrating Google AdSense, Ezoic managed mediation, affiliate conversions, and Core Web Vitals guardrails.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleKillSwitch}
              className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-extrabold shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Power className="w-4 h-4" /> EMERGENCY ADS KILL-SWITCH
            </button>
          </div>
        </div>

        {/* Global Active Mode Switcher */}
        <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700/80 flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2 text-slate-300 font-bold">
            <Sliders className="w-4 h-4 text-emerald-400" />
            <span>Active Monetization Strategy:</span>
            <span className="px-2.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono">
              {telemetry.activeMode}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {(['ADSENSE_ONLY', 'EZOIC_MANAGED', 'EZOIC_WITH_ADSENSE_MEDIATION', 'TEST_MODE', 'ADS_DISABLED'] as MonetizationMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => handleModeChange(mode)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${
                  telemetry.activeMode === mode
                    ? 'bg-emerald-500 text-slate-950 font-black shadow'
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                }`}
              >
                {mode.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Primary KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Total Gross Revenue (30D)</span>
            <DollarSign className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            ${telemetry.totalRevenue30dUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 font-semibold">
            <TrendingUp className="w-3.5 h-3.5" /> +14.8% vs previous period
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Average Page RPM / EPMV</span>
            <BarChart3 className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            ${telemetry.averagePageRpmUsd.toFixed(2)} / ${telemetry.averageEpmvUsd.toFixed(2)}
          </div>
          <div className="text-[11px] text-slate-500">
            Internal Rev / 1k Sessions: <span className="font-bold text-slate-700">${telemetry.revenuePer1kSessionsUsd.toFixed(2)}</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Consent Acceptance Rate</span>
            <ShieldAlert className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-2xl font-black text-slate-900 font-mono">
            {telemetry.consentAcceptanceRatePct}%
          </div>
          <div className="text-[11px] text-purple-700 font-semibold">
            Google-Certified TCF v2.2 & GPP Active
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-2">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center justify-between">
            <span>Core Web Vitals Impact</span>
            <Activity className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-2xl font-black text-emerald-700 font-mono">
            {telemetry.cwvGuardrailsStatus}
          </div>
          <div className="text-[11px] text-slate-500">
            Average Layout Shift (CLS): <span className="font-bold text-slate-800">0.002</span>
          </div>
        </div>
      </div>

      {/* Revenue Source Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-900 text-sm">Display Ads (AdSense / Ezoic)</h3>
            <span className="text-xs font-mono font-bold text-blue-600">
              64.3% of Total
            </span>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 font-mono">
            ${telemetry.adRevenue30dUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          <p className="text-xs text-slate-500">
            Programmatic header bidding via Ezoic managed auction with Google AdSense mediation.
          </p>
          <button
            onClick={() => onNavigateToView?.('ad-inventory-manager')}
            className="w-full text-center py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 transition-colors"
          >
            Manage Ad Inventory →
          </button>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-900 text-sm">Affiliate Revenue (Transfer)</h3>
            <span className="text-xs font-mono font-bold text-emerald-600">
              30.4% of Total
            </span>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 font-mono">
            ${telemetry.affiliateRevenue30dUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          <p className="text-xs text-slate-500">
            CPA / CPL partner commissions from verified money transfer providers (Wise, Remitly).
          </p>
          <button
            onClick={() => onNavigateToView?.('affiliate-command-center')}
            className="w-full text-center py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 transition-colors"
          >
            View Affiliate Intelligence →
          </button>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="font-bold text-slate-900 text-sm">Direct Placements & Sponsors</h3>
            <span className="text-xs font-mono font-bold text-purple-600">
              5.3% of Total
            </span>
          </div>
          <div className="text-2xl font-extrabold text-slate-900 font-mono">
            ${telemetry.sponsorshipRevenue30dUsd.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
          <p className="text-xs text-slate-500">
            Clearly labeled sponsored placements with zero influence on organic comparison algorithms.
          </p>
          <button
            onClick={() => onNavigateToView?.('commercial-governance')}
            className="w-full text-center py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 transition-colors"
          >
            Commercial Governance Hub →
          </button>
        </div>
      </div>

      {/* Commercial Integrity Unit Test Verification Panel */}
      <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-xl space-y-4">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-emerald-400" />
            <h3 className="font-bold text-sm">Automated Commercial Firewall Verification Engine</h3>
          </div>
          <span className="px-2.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 text-xs font-bold">
            All Unit Tests Passing
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-1.5">
            <div className="font-bold text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Organic Ranking Independence
            </div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {rankingCheck.message}
            </p>
          </div>

          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-1.5">
            <div className="font-bold text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Protected Calculation Zone
            </div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {protectedZoneCheck.message}
            </p>
          </div>

          <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 space-y-1.5">
            <div className="font-bold text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Financial Input Data Privacy
            </div>
            <p className="text-slate-300 text-[11px] leading-relaxed">
              {privacyCheck.message}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
